import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/client';
import toast from 'react-hot-toast';
import StatusBadge from '../../components/shared/StatusBadge';
import StaffLink from '../../components/shared/StaffLink';
import { useForm } from 'react-hook-form';
import {
  ShieldExclamationIcon,
  ShieldCheckIcon,
  PlusIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { DATE_FLOOR } from '../../lib/dateBounds';
// expiry_date is a DATE column — never a JS Date (census #194). See lib/dateOnly.js.
import { formatDateOnlyNumeric } from '../../lib/dateOnly';

export default function CompliancePage() {
  const { user, isDirector, isManagement } = useAuth();
  const [types, setTypes] = useState([]);
  const [records, setRecords] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [filterTier, setFilterTier] = useState('');
  const [filterTypeId, setFilterTypeId] = useState('');
  const [search, setSearch] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const filterStatus = searchParams.get('status') || ''; // '', 'expired', 'expiring_soon', 'alerts', 'current'
  const setFilterStatus = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next) params.set('status', next);
    else params.delete('status');
    setSearchParams(params, { replace: true });
  };
  const { register, handleSubmit, reset } = useForm();

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [typesRes, recordsRes] = await Promise.all([
        api.get('/compliance/types'),
        api.get('/compliance/records'),
      ]);
      setTypes(typesRes.data);
      setRecords(recordsRes.data);

      if (isDirector) {
        const [dashRes, staffRes] = await Promise.all([
          api.get('/compliance/dashboard'),
          api.get('/staff'),
        ]);
        setDashboard(dashRes.data);
        setStaffList(staffRes.data);
      } else if (isManagement) {
        const staffRes = await api.get('/staff');
        setStaffList(staffRes.data);
      }
    } catch (err) {
      toast.error('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (formData) => {
    try {
      await api.post('/compliance/records', {
        ...formData,
        compliance_type_id: parseInt(formData.compliance_type_id),
        user_id: formData.user_id ? parseInt(formData.user_id) : null,
      });
      toast.success('Compliance record saved');
      setShowForm(false);
      reset();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    }
  };

  const filteredRecords = records.filter(r => {
    if (filterTier && r.tier !== filterTier) return false;
    if (filterTypeId && String(r.compliance_type_id) !== String(filterTypeId)) return false;
    if (filterStatus === 'expired' && r.status !== 'expired') return false;
    if (filterStatus === 'expiring_soon' && r.status !== 'expiring_soon') return false;
    if (filterStatus === 'alerts' && r.status !== 'expired' && r.status !== 'expiring_soon') return false;
    if (filterStatus === 'current' && r.status !== 'current') return false;
    if (search) {
      const q = search.toLowerCase();
      const haystack = `${r.staff_name || ''} ${r.type_name || ''}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;
  }

  return (
    <div className="animate-slide-up">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Compliance & Licensing</h1>
          <p className="text-gray-500 text-sm">{records.length} record{records.length !== 1 ? 's' : ''}</p>
        </div>
        {isManagement && (
          <button onClick={() => setShowForm(true)} className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-xl hover:bg-orange-600 transition-colors font-medium shadow-lg shadow-orange-200/50">
            <PlusIcon className="w-5 h-5" /> Add Record
          </button>
        )}
      </div>

      {/* Dashboard alerts for director — each card toggles a filter on the table below */}
      {isDirector && dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <FilterCard
            label="Expired"
            value={dashboard.expired?.length || 0}
            color="red"
            icon={ExclamationTriangleIcon}
            active={filterStatus === 'expired' || filterStatus === 'alerts'}
            onClick={() => setFilterStatus(filterStatus === 'expired' ? '' : 'expired')}
          />
          <FilterCard
            label="Expiring Soon"
            value={dashboard.expiring_soon?.length || 0}
            color="amber"
            icon={ShieldExclamationIcon}
            active={filterStatus === 'expiring_soon' || filterStatus === 'alerts'}
            onClick={() => setFilterStatus(filterStatus === 'expiring_soon' ? '' : 'expiring_soon')}
          />
          <FilterCard
            label="Training Hours Logged"
            value={`${dashboard.training_hours?.length || 0} staff`}
            color="emerald"
            icon={ShieldCheckIcon}
            active={filterStatus === 'current'}
            onClick={() => setFilterStatus(filterStatus === 'current' ? '' : 'current')}
          />
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 mb-6 flex-wrap items-center">
        <select value={filterTier} onChange={(e) => setFilterTier(e.target.value)} className="rounded-xl border border-gray-200 px-3 py-2 text-sm bg-white focus:border-orange-500 focus:outline-none">
          <option value="">All Tiers</option>
          <option value="licensing_critical">Licensing Critical</option>
          <option value="standard">Standard</option>
        </select>
        <select value={filterTypeId} onChange={(e) => setFilterTypeId(e.target.value)} className="rounded-xl border border-gray-200 px-3 py-2 text-sm bg-white focus:border-orange-500 focus:outline-none">
          <option value="">All Types</option>
          {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by staff or item…"
          className="flex-1 min-w-[200px] rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none" />
        {(filterTier || filterTypeId || filterStatus || search) && (
          <button onClick={() => { setFilterTier(''); setFilterTypeId(''); setSearch(''); setFilterStatus(''); }}
            className="text-xs text-gray-500 hover:text-orange-500 px-2">Clear filters</button>
        )}
        <p className="text-xs text-gray-400 ml-auto">Showing {filteredRecords.length} of {records.length}</p>
      </div>

      {/* Records table */}
      {filteredRecords.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
          <p className="text-gray-500">No compliance records found</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Item</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Staff</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Tier</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Expiry</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredRecords.map((r) => (
                <tr key={r.id} className={`hover:bg-gray-50 transition-colors ${r.tier === 'licensing_critical' && r.status !== 'current' ? 'bg-red-50/30' : ''}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {r.tier === 'licensing_critical' && <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />}
                      <span className="text-sm font-medium text-gray-900">{r.type_name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {r.user_id ? (
                      <StaffLink id={r.user_id} name={r.staff_name} />
                    ) : (
                      'Center-wide'
                    )}
                  </td>
                  <td className="px-6 py-4"><StatusBadge status={r.tier} /></td>
                  <td className="px-6 py-4"><StatusBadge status={r.status} /></td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {r.expiry_date ? formatDateOnlyNumeric(r.expiry_date) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Record Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold">Add Compliance Record</h2>
              <button onClick={() => { setShowForm(false); reset(); }}><XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Compliance Type</label>
                <select {...register('compliance_type_id', { required: true })} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none bg-white">
                  <option value="">Select type</option>
                  {types.map(t => <option key={t.id} value={t.id}>{t.name} ({t.tier === 'licensing_critical' ? 'Critical' : 'Standard'})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Staff Member</label>
                <select {...register('user_id')} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none bg-white">
                  <option value="">Center-wide</option>
                  {staffList.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Issue Date</label>
                  <input type="date" min={DATE_FLOOR} {...register('issue_date')} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
                  <input type="date" min={DATE_FLOOR} {...register('expiry_date')} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea {...register('notes')} rows={3} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none resize-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" className="flex-1 bg-orange-500 text-white font-semibold py-2.5 rounded-xl hover:bg-orange-600 transition-colors">Save Record</button>
                <button type="button" onClick={() => { setShowForm(false); reset(); }} className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 transition-colors">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterCard({ label, value, color, icon: Icon, active, onClick }) {
  const styles = {
    red: {
      base: 'bg-red-50 border-red-200',
      icon: 'text-red-500',
      text: 'text-red-700',
      ring: 'ring-red-400',
    },
    amber: {
      base: 'bg-amber-50 border-amber-200',
      icon: 'text-amber-500',
      text: 'text-amber-700',
      ring: 'ring-amber-400',
    },
    emerald: {
      base: 'bg-emerald-50 border-emerald-200',
      icon: 'text-emerald-500',
      text: 'text-emerald-700',
      ring: 'ring-emerald-400',
    },
  };
  const s = styles[color];
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left ${s.base} border rounded-2xl p-5 transition-all hover:shadow-md hover:-translate-y-0.5 ${active ? `ring-2 ${s.ring}` : ''}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-5 h-5 ${s.icon}`} />
        <p className={`text-sm font-semibold ${s.text}`}>{label}</p>
      </div>
      <p className={`text-3xl font-bold ${s.text}`}>{value}</p>
      {active && (
        <p className={`text-xs mt-2 ${s.text} opacity-75`}>Tap again to clear filter</p>
      )}
    </button>
  );
}
